import { useFeatureFlagEnabled } from "@posthog/react";
import { useGetMyMentorships } from "@/lib/api/mentorships";
import { useCurrentUser } from "@/lib/api/user";
import { useAbility } from "@/lib/casl/ability-context";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view";

export interface ChatWorkspaceAccess {
  canSeeTeens: boolean;
  canSeeSupport: boolean;
  isMentorOpsSupportEnabled: boolean | undefined;
  hasMentorRole: boolean;
}

export function useChatWorkspaceAccess(): ChatWorkspaceAccess {
  const ability = useAbility();
  const { data: currentUser } = useCurrentUser();
  const mentorView = useMentorView((state) => state.mentorView);
  const isMentorOpsSupportEnabled =
    useFeatureFlagEnabled("enable-mentor-ops-support") || import.meta.env.DEV;

  const hasMentorRole = (currentUser?.systemRoles ?? []).includes("mentor");
  const canReadMessages = ability.can("read", "messages");

  const { data: mentorships } = useGetMyMentorships({
    enabled: canReadMessages && !hasMentorRole && !mentorView,
  });
  const hasOwnMentorships = (mentorships ?? []).some((m) => !m.deactivated);

  const canSeeTeens =
    canReadMessages && (hasMentorRole || mentorView || hasOwnMentorships);
  const canSeeSupport =
    ability.can("read", "internal-channels") && !!isMentorOpsSupportEnabled;

  return {
    canSeeTeens,
    canSeeSupport,
    isMentorOpsSupportEnabled,
    hasMentorRole,
  };
}
